<template>
  <AuthenticatedLayout activeMenuItem='report-platform-growth' title='Reports'>
    <LoadingLayout :isLoading='!pageReady'>
      <PaddedLayout>
        <div class='flex flex-wrap flex-col sm:flex-row py-2 justify-between sm:items-center gap-2 w-full max-w-[80rem]'>
          <h1 class='text-2xl'>Platform Growth Report</h1>
          <!-- <a :href='pdfUrl' target='_blank' type="submit" class="rounded-md bg-jananavy-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-jananavy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jananavy-600">Export PDF</a> -->
        </div>
        <div class='flex gap-2 items-center mt-2 max-w-[80rem]'>
          <TabbedNumberInput v-model='state.filters.year.from' label='Year From' :hideLabel='true' class='w-full' />
          <TabbedNumberInput v-model='state.filters.year.to' label='Year To' :hideLabel='true' class='w-full' />
        </div>
        <div class='mt-2'>
          <TabView :tabs='tabs' v-model='state.tab' />
          <div v-if='state.tab === "organisations"' class='mt-8 max-w-[80rem]'>
            <GraphPlatformGrowthLine dimension='organisation' :params='params' />
          </div>
          <div v-if='state.tab === "users"' class='mt-8 max-w-[80rem]'>
            <GraphPlatformGrowthLine dimension='user' :params='params' />
          </div>
          <div v-if='state.tab === "assets"' class='mt-8 max-w-[80rem]'>
            <GraphResponseGrowthLine dimension='platformAsset' :params='params' />
          </div>
          <div v-if='state.tab === "assetManagers"' class='mt-8 max-w-[80rem]'>
            <GraphResponseGrowthLine dimension='platformAssetManager' :params='params' />
          </div>
          <div v-if='state.tab === "engagements"' class='mt-8 max-w-[80rem]'>
            <GraphResponseGrowthLine dimension='year' :params='params' />
          </div>
        </div>
      </PaddedLayout>
    </LoadingLayout>
  </AuthenticatedLayout>
</template>

<script setup>

import { computed, reactive, watch, ref, onActivated } from 'vue';

import { debounce } from '@/kernel/debounce';
import { appendStateToRoute, getStateFromRoute } from '@/router';
import { dehydrateCollections, rehydrateCollections, getReportPDFExportUrl } from '@/kernel/report';

import AuthenticatedLayout from '@/components/layouts/authenticated-layout.vue';
import PaddedLayout from '@/components/layouts/padded-layout.vue';
import LoadingLayout from '@/components/layouts/loading-layout.vue';

import TabView from '@/components/views/tab-view.vue';

import TabbedNumberInput from '@/components/inputs/tabbed-number-input.vue';
import SingleRelationInput from '@/components/inputs/single-relation-input.vue';

import GraphResponseGrowthLine from '@/components/reports/graphs/graph-response-growth-line.vue';
import GraphPlatformGrowthLine from '@/components/reports/graphs/graph-platform-growth-line.vue';

const tabs = [
  { name: 'Organisations', key: 'organisations' },
  { name: 'Users', key: 'users' },
  { name: 'Assets', key: 'assets' },
  { name: 'Asset Managers', key: 'assetManagers' },
  { name: 'Engagements', key: 'engagements' }
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'organisations',
    filters: {
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 3,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      }
    }
  }
}

const state = reactive(getDefaultState());
const pageReady = ref(false);
const routeState = getStateFromRoute();
const pdfUrl = ref(getReportPDFExportUrl());
const params = ref(null);

const stateDidChange = (doUpdateParams = false) => {
  appendStateToRoute(state);

  setTimeout(() => {
    pdfUrl.value = getReportPDFExportUrl();
  }, 100);

  if (doUpdateParams) {
    updateParams();
  }
}

const updateParams = debounce(() => {
  params.value = calcParams();
}, 250);

const calcParams = () => {
  if (state.filters.year.from.toString().length !== 4) {
    return null;
  }

  if (state.filters.year.to.toString().length !== 4) {
    return null;
  }

  const args = {
    year: state.filters.year
  }

  return args;
}

const loadState = async () => {
  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    Object.assign(state, defaultState);
  }

  pageReady.value = true;

  watch(() => state.tab, () => {
    stateDidChange(false);
  });

  watch(() => state.filters.year.from, () => {
    stateDidChange(true);
  });

  watch(() => state.filters.year.to, () => {
    stateDidChange(true);
  });

  stateDidChange(true);
}

onActivated(() => {
  if (pageReady.value) {
    stateDidChange(false);
  } else {
    loadState();
  }
});

</script>
